// @ts-strict-ignore
import {
	AfterViewInit,
	Component,
	DoCheck,
	ElementRef,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
	output,
} from "@angular/core";
import {SimUtilityExtendedService} from "@services";
import {Media} from "@models";
import {throttle} from "utils-decorators";
import * as pdfjs from "pdfjs-dist";
import {PDFDocumentProxy, PDFPageProxy} from "pdfjs-dist";
import {RenderParameters} from "pdfjs-dist/types/src/display/api";
import * as pdfWorker from "pdfjs-dist/build/pdf.worker";

@Component({
	selector: "sim-document-viewer",
	templateUrl: "./document-viewer.component.html",
	styleUrls: ["./document-viewer.component.scss"],
	standalone: false,
})
export class DocumentViewerComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit, DoCheck {
	@Input() isPreviewMedia: boolean = false;
	@Input() previewMediaUrl: string;
	@Input() public enableAutoPlay: boolean = false;
	@Input() public autoPlayInterval: number = 15000;
	public autoPlayIntervalHandler: any = null;
	@ViewChild("pdfCanvas")
	public pdfCanvas: ElementRef<HTMLCanvasElement> = null;
	public pdfRenderContext: CanvasRenderingContext2D = null;
	@ViewChild("pdfContainer")
	public pdfContainer: ElementRef<HTMLDivElement> = null;
	public pdfUrl = "";
	public pdfDocument: PDFDocumentProxy = null;
	numberOfPdfPages = output<number>();
	pdfLoadingError = output<boolean>();
	pdfLoaded = output<boolean>();
	@Input() public media: Media = null;
	@Input() public pageNum = 1;
	private isVisible = false;
	private pageRendering = false;
	private pageNumPending: number | null = null;
	private pageScale: number = null;

	constructor(private _simUtilityService: SimUtilityExtendedService) {}

	public ngOnInit() {
		//DO NOT DELETE! THIS TELLS THE BUILD SYSTEM TO BUNDLE AND INITIALIZE PDF-JS WORKER
		pdfWorker;
	}

	public ngAfterViewInit() {
		this.pdfRenderContext = this.pdfCanvas.nativeElement.getContext("2d");
	}

	ngOnDestroy(): void {
		clearInterval(this.autoPlayIntervalHandler);
		this.destroyPdfDocument();
	}

	public ngDoCheck() {
		const nextVisibility: boolean = this.pdfContainer?.nativeElement?.offsetWidth > 0;
		if (this.isVisible !== nextVisibility) {
			this.isVisible = nextVisibility;
			if (this.isVisible && this.pdfDocument) {
				this.renderPage(this.pageNum);
			}
		}
	}

	@HostListener("window:resize", ["$event"])
	@throttle(200)
	async onResize() {
		await this.renderPage(this.pageNum);
	}

	public async ngOnChanges(changes: SimpleChanges) {
		let newPdfUrl: string = null;
		let renderRequired = false;

		if (changes["media"] && this.media) {
			newPdfUrl = this._simUtilityService.getMediaPath(this.media);
		}

		if (changes["previewMediaUrl"] && this.previewMediaUrl) {
			newPdfUrl = this.previewMediaUrl;
		}

		if (changes["pageNum"]) {
			renderRequired = true;
		}

		if (!!newPdfUrl && this.pdfUrl !== newPdfUrl) {
			this.pdfUrl = newPdfUrl;
			await this.destroyPdfDocument();
			renderRequired = true;
		}

		if (renderRequired) {
			await this.renderPage(this.pageNum);
		}

		if (changes["enableAutoPlay"]) {
			this.handleAutoPlay();
		}
	}

	private async renderPage(num: number) {
		try {
			//if another page rendering is in progress, waits until the rendering is finished.
			if (this.pageRendering) {
				this.pageNumPending = num;
				return;
			}

			this.pageRendering = true;

			//check if pdfDocument is destroyed or not loaded
			if (!this.pdfDocument || !!this.pdfDocument?.["_transport"]?.destroyed) {
				await this.loadPdf(this.pdfUrl);
				//abort if pdfDocument cannot be loaded
				if (!this.pdfDocument) {
					return;
				}
			}

			//abort if page number is not valid
			if (!(0 < num && num <= this.pdfDocument.numPages)) {
				return;
			}

			const page = await this.pdfDocument.getPage(num);
			const renderParameters = this.getRenderParameters(page);
			await page.render(renderParameters).promise;
		} catch (error) {
			console.error("Error rendering document", error);
			if (error?.name === "RenderingCancelledException") {
				console.log("retry rendering");
				await this.renderPage(num);
			}
		} finally {
			this.pageRendering = false;
		}

		if (this.pageNumPending !== null) {
			// New page rendering is pending
			num = this.pageNumPending;
			this.pageNumPending = null;
			await this.renderPage(num);
		}
	}

	private async destroyPdfDocument() {
		await this.pdfDocument?.destroy();
		this.pdfDocument = null;
	}

	private async loadPdf(url: string) {
		const pdfDocument = await pdfjs.getDocument(url).promise.catch(() => {});
		if (!pdfDocument) {
			this.pdfLoadingError.emit(true);
			return;
		}

		await this.destroyPdfDocument();
		this.pdfDocument = pdfDocument;
		this.numberOfPdfPages.emit(this.pdfDocument.numPages);
		this.pdfLoaded.emit(true);
	}

	private getRenderParameters(page: PDFPageProxy) {
		if (!page) {
			return null;
		}

		const canvas = this.pdfCanvas.nativeElement;
		const pageScale = this.pageScale || this.calculateFullScreenPageScale(page);
		const viewport = page.getViewport({scale: pageScale});

		// Support HiDPI-screens.
		const outputScale = window.devicePixelRatio || 1;

		canvas.width = Math.floor(viewport.width * outputScale);
		canvas.height = Math.floor(viewport.height * outputScale);
		canvas.style.width = Math.floor(viewport.width) + "px";
		canvas.style.height = Math.floor(viewport.height) + "px";

		const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

		return {
			canvasContext: this.pdfRenderContext,
			transform: transform,
			viewport: viewport,
		} as RenderParameters;
	}

	private calculateFullScreenPageScale(page: PDFPageProxy): number {
		const viewport = page.getViewport({scale: 1});

		const containerBoundingBox = this.pdfContainer.nativeElement.getBoundingClientRect();

		const scaleH = containerBoundingBox.height / viewport.height;
		const scaleW = containerBoundingBox.width / viewport.width;
		return Math.min(scaleH, scaleW);
	}

	private handleAutoPlay() {
		if (this.autoPlayIntervalHandler) {
			clearInterval(this.autoPlayIntervalHandler);
		}
		if (this.enableAutoPlay) {
			this.autoPlayIntervalHandler = setInterval(async () => {
				if (!this.pdfDocument) {
					return;
				}
				if (this.pageNum < this.pdfDocument.numPages) {
					this.pageNum++;
				} else {
					this.pageNum = 1;
				}
				await this.renderPage(this.pageNum);
			}, this.autoPlayInterval);
		}
	}
}
